import type { RunItem } from '../items';
import type { AgentInputItem } from '../types';
import { serializeBinary } from '../utils/binary';

export type AgentInputItemPool = Map<string, AgentInputItem[]>;

export type ReasoningItemIdPolicy = 'preserve' | 'omit';

const TOOL_CALL_RESULT_TYPE_BY_CALL_TYPE = {
  function_call: 'function_call_result',
  computer_call: 'computer_call_result',
  shell_call: 'shell_call_output',
} as const;

/**
 * 通过将原始轮次输入与所有新的运行项（不包括工具审批占位符）相结合，
 * 构建当前轮次的模型输入数组。这有助于确保对 Responses API 的重复调用
 * 只发送新生成的内容。
 *
 * 参见：https://platform.openai.com/docs/guides/conversation-state?api-mode=responses。
 */
export function getTurnInput(
  originalInput: string | AgentInputItem[],
  generatedItems: RunItem[],
  reasoningItemIdPolicy?: ReasoningItemIdPolicy,
): AgentInputItem[] {
  const outputItems = getContinuationOutputItems(
    generatedItems,
    reasoningItemIdPolicy,
  );
  return [...toAgentInputList(originalInput), ...outputItems];
}

function getContinuationOutputItems(
  generatedItems: RunItem[],
  reasoningItemIdPolicy?: ReasoningItemIdPolicy,
): AgentInputItem[] {
  const generatedOutputItems = extractOutputItemsFromRunItems(
    generatedItems,
    reasoningItemIdPolicy,
  );
  return dropOrphanToolCalls(generatedOutputItems);
}

function shouldOmitReasoningItemIds(
  reasoningItemIdPolicy?: ReasoningItemIdPolicy,
): boolean {
  return reasoningItemIdPolicy === 'omit';
}

// Extracts model-ready output items from run items, excluding approval placeholders.
export function extractOutputItemsFromRunItems(
  items: RunItem[],
  reasoningItemIdPolicy?: ReasoningItemIdPolicy,
): AgentInputItem[] {
  return extractOutputItemsFromRunItemsInternal(items, {
    omitReasoningItemIds: shouldOmitReasoningItemIds(reasoningItemIdPolicy),
  });
}

export function extractPersistableOutputItemsFromRunItems(
  items: RunItem[],
): AgentInputItem[] {
  return extractOutputItemsFromRunItemsInternal(items, {
    omitReasoningItemIds: false,
  });
}

function extractOutputItemsFromRunItemsInternal(
  items: RunItem[],
  options: { omitReasoningItemIds: boolean },
): AgentInputItem[] {
  const omitReasoningItemIds = options.omitReasoningItemIds;
  return items
    .filter(
      (item) =>
        item.type !== 'tool_approval_item' &&
        item.type !== 'compact_boundary_item' &&
        item.type !== 'compact_summary_item',
    )
    .map((item) => {
      const rawItem = withoutNullStatus(item.rawItem as AgentInputItem);
      if (!omitReasoningItemIds || item.type !== 'reasoning_item') {
        return rawItem;
      }
      if (!rawItem || typeof rawItem !== 'object' || !('id' in rawItem)) {
        return rawItem;
      }
      const { id: _id, ...withoutId } = rawItem as Record<string, unknown>;
      return withoutId as AgentInputItem;
    });
}

function withoutNullStatus(item: AgentInputItem): AgentInputItem {
  if (
    !item ||
    typeof item !== 'object' ||
    !('status' in item) ||
    (item as { status?: unknown }).status !== null
  ) {
    return item;
  }

  const { status: _status, ...withoutStatus } = item as Record<string, unknown>;
  return withoutStatus as AgentInputItem;
}

// Normalizes user-provided input into the structure the model expects. Strings become user messages,
// arrays are kept as-is so downstream loops can treat both scenarios uniformly.
export function toAgentInputList(
  originalInput: string | AgentInputItem[],
): AgentInputItem[] {
  if (typeof originalInput === 'string') {
    return [{ type: 'message', role: 'user', content: originalInput }];
  }

  return [...originalInput];
}

export function dropOrphanToolCalls(
  items: AgentInputItem[],
  options?: { pruningIndexes?: Set<number> },
): AgentInputItem[] {
  const pruningIndexes = options?.pruningIndexes;
  const completedByResultType = collectCompletedCallIdsByResultType(items);

  return items.filter((item, index) => {
    if (pruningIndexes && !pruningIndexes.has(index)) {
      return true;
    }
    if (!item || typeof item !== 'object') {
      return true;
    }
    const type = (item as { type?: unknown }).type;
    const callId = (item as { callId?: unknown }).callId;
    if (typeof type !== 'string' || typeof callId !== 'string') {
      return true;
    }
    const resultType =
      TOOL_CALL_RESULT_TYPE_BY_CALL_TYPE[
        type as keyof typeof TOOL_CALL_RESULT_TYPE_BY_CALL_TYPE
      ];
    if (!resultType) {
      return true;
    }
    return completedByResultType.get(resultType)?.has(callId) ?? false;
  });
}

function collectCompletedCallIdsByResultType(
  items: AgentInputItem[],
): Map<string, Set<string>> {
  const completed = new Map<string, Set<string>>();

  for (const item of items) {
    if (!item || typeof item !== 'object') {
      continue;
    }
    const type = (item as { type?: unknown }).type;
    const callId = (item as { callId?: unknown }).callId;
    if (typeof type !== 'string' || typeof callId !== 'string') {
      continue;
    }
    if (
      !Object.values(TOOL_CALL_RESULT_TYPE_BY_CALL_TYPE).includes(type as any)
    ) {
      continue;
    }
    const existing = completed.get(type);
    if (existing) {
      existing.add(callId);
    } else {
      completed.set(type, new Set([callId]));
    }
  }

  return completed;
}

export function getAgentInputItemKey(item: AgentInputItem): string {
  return JSON.stringify(item, agentInputSerializationReplacer);
}

export function agentInputSerializationReplacer(
  _key: string,
  value: unknown,
): unknown {
  const serialized = serializeBinary(value);
  if (serialized) {
    return serialized;
  }

  return value;
}

export function prepareModelInputItems(
  originalInput: string | AgentInputItem[],
  generatedItems: RunItem[],
  reasoningItemIdPolicy?: ReasoningItemIdPolicy,
  options?: {
    activeCompaction?: {
      baseModelContext: AgentInputItem[];
      generatedItemStartIndex: number;
    };
  },
): AgentInputItem[] {
  const activeCompaction = options?.activeCompaction;
  if (activeCompaction) {
    const preparedGeneratedItems = getContinuationOutputItems(
      generatedItems.slice(activeCompaction.generatedItemStartIndex),
      reasoningItemIdPolicy,
    );
    return [...activeCompaction.baseModelContext, ...preparedGeneratedItems];
  }

  const callerItems = toAgentInputList(originalInput);
  const preparedGeneratedItems = getContinuationOutputItems(
    generatedItems,
    reasoningItemIdPolicy,
  );
  return [...callerItems, ...preparedGeneratedItems];
}

export function buildAgentInputPool(
  items: AgentInputItem[],
): AgentInputItemPool {
  const pool: AgentInputItemPool = new Map();
  for (const item of items) {
    const key = getAgentInputItemKey(item);
    const existing = pool.get(key);
    if (existing) {
      existing.push(item);
    } else {
      pool.set(key, [item]);
    }
  }
  return pool;
}

export function removeAgentInputFromPool(
  pool: AgentInputItemPool,
  item: AgentInputItem,
): boolean {
  const key = getAgentInputItemKey(item);
  const candidates = pool.get(key);
  if (!candidates || candidates.length === 0) {
    return false;
  }
  const index = candidates.findIndex((candidate) => candidate === item);
  if (index === -1) {
    return false;
  }
  candidates.splice(index, 1);
  if (candidates.length === 0) {
    pool.delete(key);
  }
  return true;
}
